// WorkspaceBillingService.js

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';

export const WORKSPACE_BILLING_STATUS = Object.freeze({
  ACTIVE: 'active',
  CANCELED: 'canceled',
  GRACE_PERIOD: 'grace_period',
  INACTIVE: 'inactive',
  INCOMPLETE: 'incomplete',
  PAST_DUE: 'past_due',
  TRIALING: 'trialing',
  UNPAID: 'unpaid',
});

export const WORKSPACE_BILLING_PROVIDER = Object.freeze({
  NONE: 'none',
  STRIPE: 'stripe',
});

export const WORKSPACE_BILLING_INTERVAL = Object.freeze({
  MONTH: 'month',
  YEAR: 'year',
});

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeNullableString(value) {
  const normalized = normalizeString(value);
  return normalized || null;
}

function normalizeBoolean(value) {
  return Boolean(value);
}

function normalizeNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  const whole = Math.floor(parsed);
  if (whole < 0) return fallback;

  return whole;
}

function normalizeProvider(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === WORKSPACE_BILLING_PROVIDER.STRIPE) {
    return WORKSPACE_BILLING_PROVIDER.STRIPE;
  }

  return WORKSPACE_BILLING_PROVIDER.NONE;
}

function normalizeBillingStatus(value) {
  const normalized = normalizeString(value).toLowerCase();
  const allowedStatuses = new Set(Object.values(WORKSPACE_BILLING_STATUS));

  if (allowedStatuses.has(normalized)) {
    return normalized;
  }

  return WORKSPACE_BILLING_STATUS.INACTIVE;
}

function normalizeBillingInterval(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === WORKSPACE_BILLING_INTERVAL.YEAR) {
    return WORKSPACE_BILLING_INTERVAL.YEAR;
  }

  return WORKSPACE_BILLING_INTERVAL.MONTH;
}

function normalizeDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value?.toDate === 'function') {
    const dateValue = value.toDate();
    if (dateValue instanceof Date) {
      return dateValue;
    }
  }

  return null;
}

function normalizeStatusHistory(input) {
  if (!Array.isArray(input)) return [];

  return input
    .map((entry) => {
      const status = normalizeBillingStatus(entry?.status);
      const changedAt = normalizeDateValue(entry?.changedAt);
      const source = normalizeNullableString(entry?.source);

      if (!status || !changedAt) return null;

      return {
        changedAt,
        source,
        status,
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.changedAt.getTime() - left.changedAt.getTime());
}

function normalizeNowValue(value) {
  if (value instanceof Date) return value;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return new Date(numericValue);
  }

  return new Date();
}

export function cleanWorkspaceBillingRecord(docSnap) {
  const row = docSnap?.data() || {};
  const workspaceKey = normalizeString(row.workspaceKey || docSnap?.id || '');
  const billingStatus = normalizeBillingStatus(row.billingStatus || row.subscriptionStatus);
  const stripeStatus = normalizeNullableString(row.stripeStatus || row.subscriptionStatus);

  return {
    billingEmail: normalizeNullableString(row.billingEmail),
    billingName: normalizeNullableString(row.billingName),
    billingStatus,
    cancelAtPeriodEnd: normalizeBoolean(row.cancelAtPeriodEnd),
    currentPeriodEnd: normalizeDateValue(row.currentPeriodEnd),
    currentPeriodStart: normalizeDateValue(row.currentPeriodStart),
    dateCreated: normalizeDateValue(row.dateCreated),
    dateModified: normalizeDateValue(row.dateModified),
    gracePeriodEndsAt: normalizeDateValue(row.gracePeriodEndsAt),
    hasPaymentMethod: normalizeBoolean(row.hasPaymentMethod),
    id: docSnap?.id || workspaceKey,
    latestInvoiceId: normalizeNullableString(row.latestInvoiceId),
    ownerUserKey: normalizeNullableString(row.ownerUserKey),
    planCurrency: normalizeNullableString(row.planCurrency) || 'usd',
    planInterval: normalizeBillingInterval(row.planInterval),
    planKey: normalizeNullableString(row.planKey),
    planPriceId: normalizeNullableString(row.planPriceId || row.stripePriceId),
    planTitle: normalizeNullableString(row.planTitle),
    provider: normalizeProvider(row.provider),
    seatCount: normalizeNonNegativeInteger(row.seatCount, 0),
    seatCountUpdatedAt: normalizeDateValue(row.seatCountUpdatedAt),
    statusHistory: normalizeStatusHistory(row.statusHistory),
    stripeCustomerId: normalizeNullableString(row.stripeCustomerId),
    stripePriceId: normalizeNullableString(row.stripePriceId),
    stripeStatus,
    stripeSubscriptionId: normalizeNullableString(row.stripeSubscriptionId || row.subscriptionId),
    workspaceKey,
    workspaceTitle: normalizeNullableString(row.workspaceTitle),
  };
}

export function isWorkspaceBillingInGracePeriod(record) {
  return normalizeBillingStatus(record?.billingStatus) === WORKSPACE_BILLING_STATUS.GRACE_PERIOD;
}

export function isWorkspaceBillingNavigationLocked(record, now = new Date()) {
  if (!isWorkspaceBillingInGracePeriod(record)) {
    return false;
  }

  const gracePeriodEndsAt = normalizeDateValue(record?.gracePeriodEndsAt);
  if (!gracePeriodEndsAt) {
    return false;
  }

  return gracePeriodEndsAt.getTime() <= normalizeNowValue(now).getTime();
}

export async function getWorkspaceBillingRecord(workspaceKey) {
  const cleanedWorkspaceKey = normalizeString(workspaceKey);

  if (!cleanedWorkspaceKey) {
    throw new Error('workspaceKey is required');
  }

  const docSnap = await getDoc(doc(db, collections.workspaceBilling, cleanedWorkspaceKey));
  if (!docSnap.exists()) {
    return null;
  }

  return cleanWorkspaceBillingRecord(docSnap);
}

export function subscribeToWorkspaceBillingRecord(workspaceKey, callback, onError) {
  const cleanedWorkspaceKey = normalizeString(workspaceKey);

  if (!cleanedWorkspaceKey) {
    callback && callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, collections.workspaceBilling, cleanedWorkspaceKey),
    (docSnap) => {
      callback && callback(docSnap.exists() ? cleanWorkspaceBillingRecord(docSnap) : null);
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
        return;
      }

      console.error('[WorkspaceBillingService] subscribeToWorkspaceBillingRecord error:', error);
    }
  );
}

export async function upsertWorkspaceBillingRecord(workspaceKey, patch = {}) {
  const cleanedWorkspaceKey = normalizeString(workspaceKey);

  if (!cleanedWorkspaceKey) {
    throw new Error('workspaceKey is required');
  }

  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
    throw new Error('patch object is required');
  }

  const payload = {
    ...patch,
    workspaceKey: cleanedWorkspaceKey,
    dateModified: serverTimestamp(),
  };

  if (!Object.prototype.hasOwnProperty.call(payload, 'dateCreated')) {
    payload.dateCreated = serverTimestamp();
  }

  await setDoc(doc(db, collections.workspaceBilling, cleanedWorkspaceKey), payload, {
    merge: true,
  });
}